import MicroModal from 'micromodal';
import trackEvent from '@Core/tracking';

export default class BaseForm {
  constructor(
    containerEl,
    secureInputId,
    endpoint,
    submissionEventCallback = undefined,
  ) {
    this.container = containerEl;
    this.cssClasses = {
      hiddenButton: 'submit-button-hide-icon',
      invalidInput: 'input-group__invalid',
    };
    this.endpoint = endpoint;
    this.formMessageContainer = this.container.querySelector('[data-comp-form-message]');
    this.secureInputId = secureInputId;
    this.submissionButton = this.container.querySelector('[data-comp-button]');
    this.submissionButtonInitialText = this.container.querySelector('[data-comp-button-text]').innerText;

    this.requiredFieldEvents();
    this.modalEvents();

    // Some forms have events that need to happen right before submission
    this.container.addEventListener('submit', async (e) => {
      if (submissionEventCallback) {
        submissionEventCallback.call();
      }

      await this.submitForm(e);
    });
  }

  appendSecureInput() {
    if (document.getElementById(this.secureInputId)) {
      return;
    }

    const input = document.createElement('input');

    input.type = 'hidden';
    input.name = 'secure';
    input.value = 'secure';
    input.id = this.secureInputId;

    this.container.appendChild(input);
  }

  /**
   * Hides the button icon, clears the form message container
   * and changes the button text back to what it initially was
   */
  buttonEventsOnSuccess() {
    this.submissionButton.classList.add(this.cssClasses.hiddenButton);
    this.changeButtonText(this.submissionButtonInitialText);

    // Removes the form message container
    if (this.formMessageContainer && this.formMessageContainer.firstChild) {
      this.formMessageContainer.removeChild(this.formMessageContainer.firstChild);
    }
  }

  changeButtonText(text) {
    this.submissionButton.innerText = text;
  }

  handleRequest(status) {
    if (status === 200) {
      this.successfulSubmission();
      return;
    }

    this.failedSubmission();
  }

  failedSubmission() {
    // Render error messages
    renderMessage(
      this.formMessageContainer,
      '<strong>Error!</strong> Something went wrong. Please try again later while we check this out.',
    );

    this.changeButtonText('Submission Failed');
  }

  /**
   * Some forms are inside of modals
   */
  modalEvents() {
    const modalIdInput = this.container.querySelector('[name="modal_id"]');
    if (!modalIdInput) {
      return;
    }

    this.modalController = MicroModal;
    this.modalId = modalIdInput.value;
    this.modal = document.getElementById(this.modalId);
  }

  /**
   * Listens to changes on required form inputs
   * If all required inputs are have values, the button will be styled red to indicate that the form can be submitted
   * If not all required inputs are have values, the button will remain at its current color
   */
  requiredFieldEvents() {
    this.requiredInputsAreFilled = false;
    this.requiredInputs = this.container.querySelectorAll('[required]');

    // Helper function - Style invalid inputs accordingly
    const styleInvalidInputs = input => {
      if (input.validity.valid) {
        input.classList.remove(this.cssClasses.invalidInput);
      } else {
        input.classList.add(this.cssClasses.invalidInput);
      }
    };

    // Helper function - Keep track of the count for inputs that have values
    const requiredFieldListenerEvent = () => {
      const inputsWithValues = [];
      this.requiredInputs.forEach(input => {
        if (input.value !== '' && input.validity.valid) {
          inputsWithValues.push(input);
        }
      });

      this.requiredInputsAreFilled = inputsWithValues.length === this.requiredInputs.length;
      this.submissionButton.style.backgroundColor = this.requiredInputsAreFilled
        ? '#c82127' : '';
    };

    // Listener on the form to keep track of which fields have values whenever the form fields change
    this.container.addEventListener('change', () => requiredFieldListenerEvent());

    // Listener on each input to check whether the value is valid and style accordingly
    this.requiredInputs.forEach(input => {
      input.addEventListener('input', () => styleInvalidInputs(input));
    });

    // Listener on button to style invalid inputs on click
    this.submissionButton.addEventListener('click', () => {
      this.requiredInputs.forEach(input => styleInvalidInputs(input));
    });
  }

  async submitForm(e) {
    e.preventDefault();
    this.appendSecureInput();
    const formData = new FormData(e.target);

    // Update button text to indicate the form is sending
    this.changeButtonText('Sending...');

    // Disable form
    this.submissionButton.disabled = true;

    // Post to our backend
    const request = await fetch(this.endpoint, {
      method: 'post',
      body: formData,
    });

    this.handleRequest(request.status);

    // Enable form again after handling request
    this.submissionButton.disabled = false;
  }

  successfulSubmission() {
    // Render success message
    renderMessage(
      this.formMessageContainer,
      'Thanks! We have received your contact submission.',
    );

    // Make submission button icon visible again
    if (this.submissionButton.classList.contains(this.cssClasses.hiddenButton)) {
      this.submissionButton.classList.remove(this.cssClasses.hiddenButton);
    }

    // Buttons events
    this.changeButtonText('Sent');
    if (this.modal) {
      this.modal.addEventListener('animationend', () => this.buttonEventsOnSuccess());

      // Wait two seconds before closing the modal
      setTimeout(() => {
        this.modalController.close(this.modalId);
        this.modal.removeEventListener('animationend', () => this.buttonEventsOnSuccess());
      }, 2000);
    } else {
      setTimeout(() => this.buttonEventsOnSuccess(), 2000);
    }

    // Send tracking analytics
    const {
      eventCategory,
      eventAction,
      eventLabel,
    } = this.container.dataset;
    trackEvent(eventCategory, eventAction, eventLabel);
  }
}

function renderMessage(container, message) {
  container.insertAdjacentHTML('beforeend', `<p class="leading-none text-xs">${message}</p>`);
}
